import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { trackEvent } from '../utils/tracking';
import ComboBuilder from './ComboBuilder';
import toast from 'react-hot-toast';
import './Combos.css';

// One thumbnail per product in the combo, de-duplicated: a fixed combo can
// list the same product at two weights, which should not show twice.
const memberThumbs = (combo) => {
  const seen = new Set();
  return (combo.items || []).reduce((list, member) => {
    const product = member.product;
    const src = product?.thumbnail || product?.images?.[0];
    const key = String(product?._id ?? product?.id ?? src);
    if (!product || !src || seen.has(key)) return list;
    seen.add(key);
    return [...list, { key, name: product.name, src }];
  }, []);
};

// "Save with combos" — fixed bundles add straight to the cart, "pick any N"
// bundles open the builder.
const Combos = () => {
  const { addCombo, subtotal } = useCart();
  const [combos, setCombos] = useState([]);
  const [building, setBuilding] = useState(null);

  useEffect(() => {
    api.get('/combos')
      .then((r) => setCombos(r.data.combos || []))
      .catch(() => setCombos([]));
  }, []);

  if (!combos.length) return null;

  const addFixed = (combo) => {
    // A fixed combo ships exactly its member list; quantity>1 members repeat.
    const selections = [];
    (combo.items || []).forEach((member) => {
      const variant = (member.product?.variants || []).find((v) => v.weight === member.weight);
      if (!variant) return;
      for (let i = 0; i < Math.max(1, Number(member.quantity) || 1); i += 1) {
        selections.push({ product: member.product, variant });
      }
    });

    if (!selections.length) { toast.error('This combo is unavailable right now.'); return; }

    addCombo(combo, selections);
    trackEvent('add_to_cart', {
      cartValue: subtotal + (Number(combo.price) || 0),
      cartItems: selections.map((s) => ({
        productId: s.product._id ?? s.product.id,
        name: s.product.name,
        weight: s.variant.weight,
        quantity: 1,
        price: s.variant.price,
      })),
      metadata: { source: 'combo_card', comboName: combo.name, addedValue: Number(combo.price) || 0 },
    });
    toast.success(`${combo.name} added to cart`);
  };

  return (
    <section className="combos" id="combos" aria-label="Save with combos">
      <div className="combos-head">
        <div><span className="combos-eyebrow">BETTER TOGETHER</span><h2 className="combos-heading">Good things come in combos.</h2><p className="combos-intro">Your favourites, thoughtfully paired. A little more flavour for less.</p></div>
        <Link to="/combos" className="combos-all">View all combos ›</Link>
      </div>

      <div className="combos-row">
        {/* Card colour comes from the combo's category, so a veg-pickle bundle
            reads the same wherever it appears — not from its position in the
            row, which changes as combos are added or reordered. */}
        {combos.map((combo) => (
          <article key={combo._id ?? combo.id} className={`combo-card combo-card--${combo.category || 'mixed'}`}>
            {/* Name, then what is in it, then what it costs, then the button —
                read top to bottom in the order the decision is actually made. */}
            <h3 className="combo-name">{combo.name}</h3>

            <div className="combo-visual">
            {(() => {
              // Show what is actually in the combo. For a "pick any N" combo
              // the pool can be longer than the card, so it is trimmed to N
              // with a "+ more" chip — the customer chooses the rest anyway.
              const shown = 3;
              const thumbs = memberThumbs(combo).slice(0, shown);
              const extra = memberThumbs(combo).length - thumbs.length;
              if (!thumbs.length) {
                return combo.image
                  ? <img className="combo-img" src={combo.image} alt="" loading="lazy" />
                  : <span className="combo-visual-empty">A little more to love.</span>;
              }
              return (
                <div className="combo-thumbs">
                  {thumbs.map((thumb) => (
                    <span key={thumb.key} className="combo-thumb" title={thumb.name}>
                      <img src={thumb.src} alt={thumb.name} loading="lazy" />
                    </span>
                  ))}
                  {extra > 0 && <span className="combo-thumb combo-thumb--more">+{extra}</span>}
                </div>
              );
            })()}
            </div>

            <div className="combo-price">
              <strong>&#8377;{Number(combo.price).toLocaleString('en-IN')}</strong>
              {combo.compareAtPrice > combo.price && (
                <span className="combo-mrp">&#8377;{Number(combo.compareAtPrice).toLocaleString('en-IN')}</span>
              )}
            </div>

            {combo.type === 'pick' ? (
              <button className="btn btn-outline btn-sm add-to-cart-btn" aria-label={`Build ${combo.name}`} onClick={() => setBuilding(combo)}>
                Build Combo
              </button>
            ) : (
              <button className="btn btn-gold btn-sm add-to-cart-btn" aria-label={`Add ${combo.name} to cart`} onClick={() => addFixed(combo)}>
                Add to Cart
              </button>
            )}
          </article>
        ))}
      </div>

      {building && <ComboBuilder combo={building} onClose={() => setBuilding(null)} />}
    </section>
  );
};

export default Combos;
